import { CompoundEntityRef, Entity } from '@backstage/catalog-model';

import { parseTierConfig } from './tiers/parseTierConfig';
import { ResolvedTierConfig } from './tiers/types';

/**
 * The slice of the catalog API the tier-config cache needs (structural, so
 * tests don't have to mock the full `CatalogApi` surface).
 */
export type CatalogEntityLookup = {
  getEntityByRef(entityRef: CompoundEntityRef): Promise<Entity | undefined>;
};

/**
 * Session-scoped cache of resolved tier configurations, keyed per entity
 * (Story 6.3, AC 7 — same pattern as `versionsCache.ts`). The redirect
 * wrapper and both header addons read through this so each entity's catalog
 * record is fetched and parsed at most once per session.
 *
 * Resolves to `null` — fail closed, Epic 9 behavior — when the entity is
 * genuinely missing, the catalog lookup keeps failing after retries, or the
 * tier annotation does not parse (AC 4). An entity with no tier annotation
 * resolves to the implicit single `internal` tier via the 6.2 parser, not to
 * `null`. Final failures are cached so a broken entity doesn't retry-storm.
 *
 * Since Story 6.4 activated the implicit-internal default, a `null` here
 * makes the reader fall back to the pre-tier Epic 9 redirect rule, which
 * treats a real tier id (e.g. `internal`) as an ordinary page-path segment —
 * turning a valid `<tier>/latest/...` URL into a broken `latest/<tier>/...`
 * redirect (production incident 2026-07-16, PE-120 follow-up). A one-off
 * catalog blip transiently rejecting `getEntityByRef` was previously
 * indistinguishable from a genuinely-missing entity, and got cached as null
 * for the rest of the session either way. Retrying a throwing lookup before
 * giving up closes that gap; a lookup that cleanly resolves to `undefined`
 * (the entity really isn't in the catalog) is not retried — that outcome is
 * deterministic, not transient.
 */
const cache = new Map<string, Promise<ResolvedTierConfig | null>>();

/** Extra attempts after a throwing `getEntityByRef` call, before failing closed. */
const RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 300;

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function getEntityWithRetry(
  catalogApi: CatalogEntityLookup,
  entityRef: CompoundEntityRef,
): Promise<Entity | undefined> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await catalogApi.getEntityByRef(entityRef);
    } catch (error) {
      if (attempt >= RETRY_ATTEMPTS) {
        throw error;
      }
      await delay(RETRY_DELAY_MS);
    }
  }
}

function entityKey(ref: CompoundEntityRef): string {
  return `${ref.kind}:${ref.namespace}:${ref.name}`.toLowerCase();
}

export function fetchTierConfig(
  catalogApi: CatalogEntityLookup,
  entityRef: CompoundEntityRef,
): Promise<ResolvedTierConfig | null> {
  const key = entityKey(entityRef);
  let pending = cache.get(key);
  if (!pending) {
    pending = getEntityWithRetry(catalogApi, entityRef)
      .then(entity => {
        if (!entity) {
          return null;
        }
        const result = parseTierConfig(entity.metadata.annotations);
        return result.ok ? result.config : null;
      })
      .catch(() => null);
    cache.set(key, pending);
  }
  return pending;
}

/** Test-only: clear the session cache between cases. */
export function __clearTiersCache(): void {
  cache.clear();
}
